class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.overlay = this.querySelector('[id^="CartDrawer-Overlay"]');
    this.overlay?.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.setPointerLeaveAccessibility();

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:select', (e) => {
        if (e.target.matches('.section-cart-drawer')) this.open();
      });

      document.addEventListener('shopify:section:deselect', (e) => {
        if (e.target.matches('.section-cart-drawer')) this.close();
      });
    }
  }

  setPointerLeaveAccessibility() {
    if (!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) return;
    const content = this.querySelector('[data-drawer-content]');
    if (!content || content.dataset.pointerLeaveReady === 'true') return;
    content.dataset.pointerLeaveReady = 'true';

    const hasOpenYmqEditor = () => {
      const modal = document.querySelector('.ymq_modal__container');
      if (!modal) return false;
      const style = window.getComputedStyle(modal);
      return style.display !== 'none' && style.visibility !== 'hidden' && modal.getClientRects().length > 0;
    };

    content.addEventListener('pointerenter', () => {
      window.clearTimeout(this.pointerLeaveTimer);
    });
    content.addEventListener('pointerleave', () => {
      window.clearTimeout(this.pointerLeaveTimer);
      this.pointerLeaveTimer = window.setTimeout(() => {
        if (hasOpenYmqEditor()) return;
        if (this.classList.contains('active') || this.classList.contains('open')) this.close();
      }, 300);
    });
  }

  connectedCallback() {
    // 注意：data-moved="false" 是字符串(真值)，原来的 !this.dataset.moved 恒为 false → 永不移动。
    // 必须显式判断字符串 'true'，否则抽屉留在 section 容器里被 header/闪屏的层叠上下文压住。
    if (this.dataset.moved !== 'true') {
      this.dataset.moved = 'true';
      document.body.appendChild(this);
    }
    window.setTimeout(() => this.setPointerLeaveAccessibility(), 0);
    window.setTimeout(() => initRoundedDrawerPricing(this), 0);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('.cart-icon--drawer');
    cartLink?.setAttribute('role', 'button');
    cartLink?.setAttribute('aria-haspopup', 'dialog');
    cartLink?.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink?.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  async refreshIfStale() {
    const cartResponse = await fetch('/cart.js?_=' + Date.now(), {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!cartResponse.ok) return false;

    const cart = await cartResponse.json();
    const renderedRows = this.querySelectorAll('#CartDrawer-CartItems .cart-item').length;
    const renderedCount = parseInt(this.querySelector('[data-cart-text]')?.textContent || '', 10) || 0;
    const expectedRows = Array.isArray(cart.items) ? cart.items.length : 0;
    const shouldBeEmpty = cart.item_count === 0;
    const stateMatches =
      renderedRows === expectedRows &&
      renderedCount === cart.item_count &&
      this.classList.contains('is-empty') === shouldBeEmpty;
    if (stateMatches) return false;

    const sectionIds = this.getSectionsToRender().map((section) => section.id);
    const retryDelays = [0, 150, 300, 600, 1000, 1500];
    let sections = null;
    for (const delay of retryDelays) {
      if (delay) await new Promise((resolve) => window.setTimeout(resolve, delay));
      const sectionResponse = await fetch((window.routes && window.routes.cart_update_url) || '/cart/update.js', {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
        body: JSON.stringify({
          updates: {},
          sections: sectionIds,
          sections_url: window.location.pathname,
        }),
      });
      if (!sectionResponse.ok) continue;

      const sectionData = await sectionResponse.json();
      const candidate = sectionData.sections || sectionData;
      const drawerHtml = candidate['cart-drawer'];
      if (typeof drawerHtml !== 'string') continue;
      const drawerDocument = new DOMParser().parseFromString(drawerHtml, 'text/html');
      const candidateDrawer = drawerDocument.querySelector('#Cart-Drawer, cart-drawer');
      const candidateRows = drawerDocument.querySelectorAll('#CartDrawer-CartItems .cart-item').length;
      const candidateCount = parseInt(drawerDocument.querySelector('[data-cart-text]')?.textContent || '', 10) || 0;
      const candidateEmpty = candidateDrawer?.classList.contains('is-empty') || false;
      if (
        candidateRows === expectedRows &&
        candidateCount === cart.item_count &&
        candidateEmpty === shouldBeEmpty
      ) {
        sections = candidate;
        break;
      }
    }
    if (!sections) return false;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (sectionElement && sections[section.id]) {
        sectionElement.innerHTML = this.getSectionInnerHTML(sections[section.id], section.selector);
      }
    });
    this.syncRenderedCartState();
    return true;
  }

  open(triggeredBy, skipRefresh = false) {
    if (!skipRefresh) {
      if (!this.refreshPromise) {
        this.refreshPromise = this.refreshIfStale()
          .catch((error) => console.warn('Unable to refresh cart drawer before opening.', error))
          .finally(() => {
            this.refreshPromise = null;
          });
      }
      this.refreshPromise.finally(() => this.open(triggeredBy, true));
      return;
    }

    window.clearTimeout(this.pointerLeaveTimer);
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    const cartDrawerDirection = this.getAttribute("data-drawer-direction");
    const contentElement = this.querySelector("[data-drawer-content]");

    this.classList.add("open");

    let rtl = typeof theme !== "undefined" && theme.config && theme.config.rtl === true;
    let drawerDirection = cartDrawerDirection;

    if (rtl) {
      drawerDirection = drawerDirection === "left" ? "right" : "left";
    }

    Motion.timeline([
      [
        contentElement,
        {
          opacity: [0, 1],
          transform:
            drawerDirection === "left"
              ? ["translateX(-100%)", "translateX(0)"]
              : ["translateX(100%)", "translateX(0)"],
        },
        { duration: 0.3, easing: [0.61, 0.22, 0.23, 1], at: "-0.05" },
      ],
    ]);

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
    document.documentElement.setAttribute('scroll-lock', '');
    [0, 180, 600].forEach((delay) => {
      window.setTimeout(() => scheduleRoundedDrawerPricing(this), delay);
    });
  }

  syncRenderedCartState() {
    const hasItems = !!this.querySelector('#CartDrawer-CartItems .cart-item');
    this.classList.toggle('is-empty', !hasItems);
    this.querySelector('cart-drawer-items')?.classList.toggle('is-empty', !hasItems);
    const checkoutButton = this.querySelector('#CartDrawer-Checkout');
    if (checkoutButton) checkoutButton.disabled = !hasItems;
    this.overlay = this.querySelector('[id^="CartDrawer-Overlay"]');
    this.overlay?.addEventListener('click', this.close.bind(this));
    this.setPointerLeaveAccessibility();
  }

  async close() {
    window.clearTimeout(this.pointerLeaveTimer);
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.documentElement.removeAttribute('scroll-lock');
    const cartDrawerDirection = this.getAttribute("data-drawer-direction");
    const contentElement = this.querySelector("[data-drawer-content]");

    let rtl = typeof theme !== "undefined" && theme.config && theme.config.rtl === true;
    let drawerDirection = cartDrawerDirection;
    
    if (rtl) {
      drawerDirection = drawerDirection === "left" ? "right" : "left";
    }

    await Motion.timeline([
      [
        contentElement,
        {
          opacity: [1, 0],
          transform:
            drawerDirection === "left"
              ? ["translateX(0)", "translateX(-100%)"]
              : ["translateX(0)", "translateX(100%)"],
        },
        { duration: 0.3, easing: [0.61, 0.22, 0.23, 1] },
      ]
    ]).finished;

    this.classList.remove("open");
    this.classList.remove('active');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    
    const cartItemsContainer = this.querySelector('#CartDrawer-CartItems');
    const popularProductsBlock = cartItemsContainer?.querySelector('.cart-drawer__recommendations');
    const preservedPopularProducts = popularProductsBlock ? popularProductsBlock.outerHTML : null;
    
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
      if (sectionElement && parsedState.sections && parsedState.sections[section.id]) {
        sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      }
    });

    if (preservedPopularProducts) {
      const newCartItemsContainer = this.querySelector('#CartDrawer-CartItems');
      const newPopularProductsBlock = newCartItemsContainer?.querySelector('.cart-drawer__recommendations');
      if (!newPopularProductsBlock && newCartItemsContainer) {
        const statusElements = newCartItemsContainer.querySelectorAll('p[role="status"]');
        if (statusElements.length > 0) {
          statusElements[0].insertAdjacentHTML('beforebegin', preservedPopularProducts);
        } else {
          newCartItemsContainer.insertAdjacentHTML('beforeend', preservedPopularProducts);
        }
      }
    }

    this.syncRenderedCartState();

    setTimeout(() => {
      initRoundedDrawerPricing(this);
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}
if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

function readDrawerYmqAmount(element) {
  if (!element) return NaN;
  const attributeValue = element.getAttribute('ymq-b2b-money') || element.getAttribute('data-ymq-money');
  if (attributeValue !== null) return parseFloat(attributeValue);

  const matches = element.textContent.trim().match(/[\d,.]+/g);
  return matches?.length ? parseFloat(matches[matches.length - 1].replace(/,/g, '')) : NaN;
}

const drawerRoundedUnitCache = new Map();

function getDrawerItemKey(row) {
  const line = row.getAttribute('data-line') || '';
  const variantId = row.querySelector('[data-quantity-variant-id]')?.getAttribute('data-quantity-variant-id') || '';
  return line && variantId ? `${line}:${variantId}` : '';
}

function formatDrawerMoney(amount, reference) {
  if (window.ParansMoney && typeof window.ParansMoney.formatUsdAmount === 'function') {
    return window.ParansMoney.formatUsdAmount(amount);
  }

  const currency = window.Shopify?.currency?.active;
  const locale = document.documentElement.lang || 'en';

  if (currency && typeof Intl !== 'undefined' && Intl.NumberFormat) {
    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
    } catch (error) {}
  }

  const text = reference?.textContent.trim() || '';
  const number = text.match(/[\d.,]+/);
  return number ? text.replace(number[0], amount.toFixed(2)) : amount.toFixed(2);
}

function getDrawerMoneyNode(wrapper) {
  return wrapper?.querySelector('[ymq-b2b-money]') || wrapper;
}

function writeDrawerMoney(element, amount) {
  if (!element) return;
  const rawAmount = String(amount);
  ['ymq-b2b-money', 'data-ymq-money', 'data-parans-hextom-raw'].forEach((attribute) => {
    if (element.hasAttribute(attribute) && element.getAttribute(attribute) !== rawAmount) {
      element.setAttribute(attribute, rawAmount);
    }
  });

  if (window.ParansMoney && typeof window.ParansMoney.applyUsdAmount === 'function') {
    window.ParansMoney.applyUsdAmount(element, amount);
    return;
  }

  const formatted = formatDrawerMoney(amount, element);
  if (element.textContent !== formatted) element.textContent = formatted;
}

function syncRoundedDrawerPricing(drawer) {
  if (!(drawer instanceof HTMLElement)) return;

  let subtotal = 0;
  let hasRoundedLine = false;

  drawer.querySelectorAll('#CartDrawer .cart-item').forEach((row) => {
    const quantityBreakNodes = Array.from(row.querySelectorAll('.ymq-b2b-cart-qb-price [ymq-b2b-money]'));
    const discountedUnitNodes = quantityBreakNodes.filter((node) => !node.closest('.cart-item__totals'));
    const discountedLineNodes = quantityBreakNodes.filter((node) => node.closest('.cart-item__totals'));
    const originalUnitNodes = Array.from(row.querySelectorAll('[ymq-b2b-item-original-price]'))
      .map(getDrawerMoneyNode);
    const originalLineNodes = Array.from(row.querySelectorAll('[ymq-b2b-item-original-line-price]'))
      .map(getDrawerMoneyNode);
    const unitNodes = discountedUnitNodes.length ? discountedUnitNodes : originalUnitNodes;
    const lineNodes = discountedLineNodes.length ? discountedLineNodes : originalLineNodes;
    const quantityInput = row.querySelector('input[name="updates[]"], input[type="number"]');
    const quantity = parseInt(quantityInput?.value, 10);
    const itemKey = getDrawerItemKey(row);
    const cachedUnit = drawerRoundedUnitCache.get(itemKey);
    const unitMoney = unitNodes[unitNodes.length - 1];
    const lineMoney = lineNodes[lineNodes.length - 1];
    const liveUnit = readDrawerYmqAmount(unitMoney);
    const cachedUnitMatches = cachedUnit?.quantity === quantity;
    const unit = discountedUnitNodes.length && Number.isFinite(liveUnit)
      ? liveUnit
      : cachedUnitMatches
        ? cachedUnit.unit
        : liveUnit;

    if (!lineMoney || !Number.isFinite(unit) || Number.isNaN(quantity)) return;

    if (itemKey && Number.isFinite(liveUnit) && (discountedUnitNodes.length || !cachedUnit)) {
      drawerRoundedUnitCache.set(itemKey, { quantity, unit: liveUnit });
    }

    const roundedUnit = Math.round((unit + Number.EPSILON) * 100) / 100;
    const roundedLine = Math.round(roundedUnit * quantity * 100) / 100;

    hasRoundedLine = true;
    subtotal += roundedLine;
    unitNodes.forEach((node) => writeDrawerMoney(node, roundedUnit));
    lineNodes.forEach((node) => writeDrawerMoney(node, roundedLine));
  });

  if (!hasRoundedLine) return;

  subtotal = Math.round(subtotal * 100) / 100;
  const totalContainer = drawer.querySelector('.totals [ymq-b2b-cart-total-price]');
  const totalMoney = totalContainer?.querySelector('[ymq-b2b-money]') || totalContainer;
  if (!totalMoney) return;

  const couponDiscount = Array.from(
    drawer.querySelectorAll('.totals .ymq-b2b-cart-discount-price')
  ).reduce((total, wrapper) => {
    const amount = readDrawerYmqAmount(wrapper.querySelector('[ymq-b2b-money]') || wrapper);
    return total + (Number.isFinite(amount) ? amount : 0);
  }, 0);
  const combinedTotal = Math.max(0, subtotal - couponDiscount);

  writeDrawerMoney(totalMoney, combinedTotal);
  drawer.dataset.paransRoundedTotal = combinedTotal.toFixed(2);
}

function scheduleRoundedDrawerPricing(drawer) {
  if (!(drawer instanceof HTMLElement) || drawer.paransDrawerPriceFrame) return;
  drawer.paransDrawerPriceFrame = window.requestAnimationFrame(() => {
    drawer.paransDrawerPriceFrame = 0;
    syncRoundedDrawerPricing(drawer);
  });
}

function initRoundedDrawerPricing(drawer) {
  if (!(drawer instanceof HTMLElement)) return;
  scheduleRoundedDrawerPricing(drawer);
  if (drawer.paransDrawerPriceObserver) return;

  drawer.paransDrawerPriceObserver = new MutationObserver(() => scheduleRoundedDrawerPricing(drawer));
  drawer.paransDrawerPriceObserver.observe(drawer, {
    attributes: true,
    characterData: true,
    childList: true,
    subtree: true,
  });
}

class ModalComponent extends HTMLElement {
  constructor() {
    super();
    this.querySelector('#Modal-Overlay')?.addEventListener('click', this.close.bind(this));

    const modalId = `#${this.id}`;
    const toggleButtons = document.querySelectorAll(`[data-drawer-toggle="${modalId}"]`);

    toggleButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.isOpen ? this.close() : this.show(button);
      });
    });

    // Shopify Design Mode
    if (Shopify.designMode && this.hasAttribute('check-shopify-design-mode')) {
      this.addEventListener('shopify:block:select', () => (this.isOpen = true));
      this.addEventListener('shopify:block:deselect', () => (this.isOpen = false));
    }
  }

  connectedCallback() {
    // 注意：data-moved="false" 是字符串(真值)，原来的 !this.dataset.moved 恒为 false → 永不移动。
    // 必须显式判断字符串 'true'，否则抽屉留在 section 容器里被 header/闪屏的层叠上下文压住。
    if (this.dataset.moved !== 'true') {
      this.dataset.moved = 'true';
      document.body.appendChild(this);
    }
  }

  static get observedAttributes() {
    return ['open'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open') {
      this.setAttribute('aria-expanded', newValue === '' ? 'true' : 'false');
    }
  }

  get isOpen() {
    return this._isOpen;
  }

  set isOpen(value) {
    if (value !== this._isOpen) {
      this._isOpen = value;
      if (this.isConnected) {
        this._animate(value);
      } else {
        value ? this.setAttribute('open', '') : this.removeAttribute('open');
      }
    }
    this.setAttribute('aria-expanded', value ? 'true' : 'false');
  }

  show(opener) {
    this.openedBy = opener;
    this.isOpen = true;
    this.trapFocus();
  }

  close() {
    this.isOpen = false;
    removeTrapFocus(this.openedBy);
  }

  async _animate(open) {
    const inner = this.querySelector('.modal__inner');
    const content = this.querySelector('.modal-content');
    const header = this.querySelector('.modal-header');
    this.style.overflow = 'hidden';

    if (open) {
      this.setAttribute('open', '');
      await Motion.timeline([
        [inner, { opacity: [0, 1], transform: ['translateY(2rem)', 'translateY(0)'] }, { duration: 0.4, easing: 'cubic-bezier(0.7, 0, 0.3, 1)' }],
        [content, { opacity: [0, 1], transform: ['translateY(1rem)', 'translateY(0)'] }, { duration: 0.3, at: '-0.15' }],
        [header, { opacity: [0, 1], transform: ['translateY(-0.5rem)', 'translateY(0)'] }, { duration: 0.3, at: '-0.2' }],
      ]).finished;
    } else {
      await Motion.timeline([
        [header, { opacity: [1, 0], transform: ['translateY(0)', 'translateY(-0.5rem)'] }, { duration: 0.2 }],
        [content, { opacity: [1, 0], transform: ['translateY(0)', 'translateY(1rem)'] }, { duration: 0.2 }],
        [inner, { opacity: [1, 0], transform: ['translateY(0)', 'translateY(2rem)'] }, { duration: 0.25, easing: 'cubic-bezier(0.7, 0, 0.3, 1)' }],
      ]).finished;
      this.removeAttribute('open');
    }

    this.style.overflow = 'visible';
  }

  trapFocus() {
    this.addEventListener(
      'transitionend',
      () => {
        const container = this.querySelector('.modal__inner');
        const focusEl = this.querySelector('.modal__close');
        trapFocus(container, focusEl);
      },
      { once: true }
    );
  }
}

if (!customElements.get('modal-component')) {
  customElements.define('modal-component', ModalComponent);
}

const resyncYmqDrawerPricing = async () => {
  try {
    const response = await fetch(`${routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return;

    const cart = await response.json();
    if (typeof window.ymqOption?.cartUpdate === 'function') {
      await window.ymqOption.cartUpdate(cart, true);
    } else if (typeof window.ymqOption?.doUpdateCartElement === 'function') {
      window.ymqOption.doUpdateCartElement();
    }
    document.querySelectorAll('cart-drawer').forEach(scheduleRoundedDrawerPricing);
  } catch (error) {
    console.warn('Unable to resync cart pricing after discount update.', error);
  }
};

document.addEventListener('click', (event) => {
  const removeButton = event.target.closest(
    '[ymq-option-action="discount-remove"], .ymq-option-discount-remove'
  );
  if (!removeButton?.closest('cart-drawer')) return;

  const discountBadge = removeButton.closest('.ymq-option-discount-badge');
  window.setTimeout(() => discountBadge?.remove(), 0);

  [180, 600, 1300].forEach((delay) => {
    window.setTimeout(resyncYmqDrawerPricing, delay);
  });
});

document.addEventListener('DiscountUpdateEvent', () => {
  [180, 600, 1300].forEach((delay) => {
    window.setTimeout(resyncYmqDrawerPricing, delay);
  });
});

const setParansDrawerCouponStatus = (container, message, state) => {
  const status = container?.querySelector('.parans-drawer-coupon__status');
  if (!status) return;
  status.textContent = message;
  status.dataset.state = state || '';
};

const getParansDrawerCouponCodes = (container) =>
  Array.from(container?.querySelectorAll('[data-parans-drawer-discount-code]') || [])
    .map((pill) => (pill.dataset.paransDrawerDiscountCode || '').trim())
    .filter(Boolean);

const normalizeParansDrawerCouponCode = (code) => String(code || '').trim().toLowerCase();

const requestParansDrawerCouponUpdate = async (discount, sectionId, includeSections = true) => {
  const body = { discount };
  if (includeSections) {
    body.sections = [sectionId];
    body.sections_url = window.location.pathname;
  }

  const response = await fetch(`${routes.cart_update_url}`, {
    ...fetchConfig('json'),
    body: JSON.stringify(body),
  });
  const responseText = await response.text();
  let data = {};

  try {
    data = responseText ? JSON.parse(responseText) : {};
  } catch (error) {
    throw new Error('discount response was not valid JSON');
  }

  if (!response.ok || data.errors) {
    throw new Error(data.description || data.message || 'discount update failed');
  }

  return data;
};

const updateParansDrawerCoupon = async (container, nextCodes, trigger, options = {}) => {
  const sectionId = container?.dataset.sectionId;
  if (!sectionId || !trigger || container.dataset.couponBusy === 'true') return;

  const previousCodes = options.previousCodes || getParansDrawerCouponCodes(container);
  const addedCode = options.addedCode || '';
  const discount = nextCodes.join(',');

  container.dataset.couponBusy = 'true';
  trigger.setAttribute('aria-busy', 'true');
  setParansDrawerCouponStatus(container, '', '');

  try {
    const data = await requestParansDrawerCouponUpdate(discount, sectionId);
    const returnedCodes = new Map(
      (Array.isArray(data.discount_codes) ? data.discount_codes : []).map((item) => [
        normalizeParansDrawerCouponCode(item.code),
        item,
      ])
    );
    const rejectedCodes = nextCodes.filter((code) => {
      const result = returnedCodes.get(normalizeParansDrawerCouponCode(code));
      return !result || result.applicable === false;
    });

    if (addedCode && rejectedCodes.length > 0) {
      await requestParansDrawerCouponUpdate(previousCodes.join(','), sectionId, false);
      const hasExistingCodes = previousCodes.length > 0;
      setParansDrawerCouponStatus(
        container,
        hasExistingCodes
          ? 'Shopify did not combine this code with the discounts already applied. Your existing discounts were kept.'
          : 'Shopify did not apply this code. It is invalid or not eligible for the current cart.',
        'error'
      );
      return;
    }

    document.dispatchEvent(new CustomEvent('DiscountUpdateEvent', { detail: { data, id: sectionId } }));
    const cartItems = document.querySelector('cart-drawer-items');
    if (data.sections && typeof cartItems?.updateSections === 'function') {
      cartItems.updateSections(data.sections);
    }
    [180, 600, 1300].forEach((delay) => {
      window.setTimeout(resyncYmqDrawerPricing, delay);
    });
    window.setTimeout(() => {
      const nextContainer = document.querySelector(
        `[data-parans-drawer-coupon][data-section-id="${sectionId}"]`
      );
      setParansDrawerCouponStatus(nextContainer, options.successMessage || '', options.successMessage ? 'success' : '');
    }, 50);
  } catch (error) {
    setParansDrawerCouponStatus(
      container,
      'Shopify could not update the discount codes. Please check the code and try again.',
      'error'
    );
  } finally {
    delete container.dataset.couponBusy;
    trigger.removeAttribute('aria-busy');
  }
};

document.addEventListener('submit', (event) => {
  const form = event.target.closest?.('.parans-drawer-coupon__form');
  if (!form) return;

  event.preventDefault();
  const container = form.closest('[data-parans-drawer-coupon]');
  const input = form.querySelector('input[name="discount"]');
  const button = form.querySelector('.parans-drawer-coupon__apply');
  const code = input?.value.trim() || '';
  const existingCodes = getParansDrawerCouponCodes(container);

  if (!code) {
    setParansDrawerCouponStatus(container, 'Enter a discount code first.', 'error');
    input?.focus();
    return;
  }

  if (existingCodes.some((item) => normalizeParansDrawerCouponCode(item) === normalizeParansDrawerCouponCode(code))) {
    setParansDrawerCouponStatus(container, 'This discount code is already applied.', 'error');
    input?.select();
    return;
  }

  updateParansDrawerCoupon(container, [...existingCodes, code], button, {
    previousCodes: existingCodes,
    addedCode: code,
    successMessage: 'Discount code applied.',
  });
});

document.addEventListener('click', (event) => {
  const removeButton = event.target.closest?.('[data-parans-drawer-remove-coupon]');
  if (!removeButton) return;

  const container = removeButton.closest('[data-parans-drawer-coupon]');
  const code = (removeButton.dataset.discountCode || '').toLowerCase();
  const existingCodes = getParansDrawerCouponCodes(container);
  const remainingCodes = existingCodes
    .filter((item) => item && item.toLowerCase() !== code);

  updateParansDrawerCoupon(container, remainingCodes, removeButton, {
    previousCodes: existingCodes,
    successMessage: 'Discount code removed.',
  });
});
